import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { invitationsApi, apiErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Sheet } from "@/components/Sheet";
import { toast } from "sonner";

export function InviteDialog({ open, onOpenChange, onCreated }) {
  const { isAdmin } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales_user");
  const [displayName, setDisplayName] = useState("");
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setEmail(""); setRole("sales_user"); setDisplayName(""); setLink(""); setCopied(false); };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await invitationsApi.create({ email, role, display_name: displayName || null });
      const url = `${window.location.origin}${data.invitation_link_path}`;
      setLink(url);
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  const copy = () => { navigator.clipboard?.writeText(link); setCopied(true); toast.success("Link copied"); };

  return (
    <Sheet open={open} onClose={() => { reset(); onOpenChange(false); }} title="Invite a user" testid="invite-dialog">
      {!link ? (
        <>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Email</label>
          <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="rep@company.com" />
          <label className="mb-1 block text-xs font-semibold text-slate-600">Role</label>
          <select data-testid="invite-role-select" value={role} onChange={(e) => setRole(e.target.value)}
            className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
            <option value="sales_user">Sales user</option>
            {isAdmin && <option value="sales_manager">Sales manager</option>}
            {isAdmin && <option value="admin">Admin</option>}
          </select>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Display name (optional)</label>
          <input data-testid="invite-name-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            className="mb-4 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="Jane Rep" />
          <button data-testid="invite-submit" onClick={submit} disabled={saving || !email}
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Creating…" : "Create invitation"}
          </button>
        </>
      ) : (
        <div data-testid="invite-link-result">
          <p className="mb-2 text-sm text-slate-600">Copy this link and send it to the invitee. It is shown <strong>once</strong>.</p>
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
            <input readOnly value={link} data-testid="invite-link-value" className="min-w-0 flex-1 bg-transparent text-xs text-slate-700 outline-none" />
            <button data-testid="invite-copy" onClick={copy} className="shrink-0 rounded-md bg-teal-700 p-2 text-white hover:bg-teal-800">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-amber-600">Note: email delivery is not part of Phase 2b — share the link manually.</p>
          <button data-testid="invite-done" onClick={() => { reset(); onOpenChange(false); }}
            className="mt-4 min-h-[48px] w-full rounded-lg border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50">Done</button>
        </div>
      )}
    </Sheet>
  );
}
